import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../utils/constants';
import {
  CHUNK_SIZE_MIN,
  CHUNK_SIZE_MAX,
  CHUNK_SIZE_DEFAULT,
  CHUNK_SIZE_STEP,
  SPEED_THRESHOLD_FAST,
  SPEED_THRESHOLD_SLOW,
  SPEED_SAMPLES_TO_CONSIDER,
  CHUNK_UPLOAD_TIMEOUT_MS,
  RETRY_TIMEOUT_MULTIPLIER,
  MAX_RETRY_ATTEMPTS,
  RETRY_DELAY_MS,
} from '../utils/constants';
import { isFileTypeSupported } from '../utils/fileValidation';
import {
  saveUploadSession as saveToIndexedDB,
  getAllUploadSessions,
  deleteUploadSession as deleteFromIndexedDB,
  clearAllSessions as clearAllFromIndexedDB,
} from '../utils/indexedDB';

/**
 * Custom hook for managing file uploads
 * Handles upload sessions, chunk uploads, pause/resume, and adaptive chunk sizing
 */
// Maximum concurrent uploads
const MAX_CONCURRENT_UPLOADS = 3;

export function useFileUpload(selectedProject, formData = null, { onAllUploadsSettled } = {}) {
  const [uploads, setUploads] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Refs for upload management
  const abortControllersRef = useRef(new Map());
  const pauseStatusRef = useRef(new Map());
  const fileRefsRef = useRef(new Map());
  const fileInputRef = useRef(null);
  const filesSelectedForProjectRef = useRef(null);
  const chunkSizeRef = useRef(new Map());
  const uploadSpeedRef = useRef(new Map());
  const uploadTimeHistoryRef = useRef(new Map()); // Track upload times for average calculation
  const activeUploadsRef = useRef(new Set()); // Track active upload session IDs
  const onAllUploadsSettledRef = useRef(onAllUploadsSettled);
  onAllUploadsSettledRef.current = onAllUploadsSettled;

  // Clear everything on page reload
  useEffect(() => {
    const clearOnReload = async () => {
      try {
        // Clear all uploads state
        setUploads([]);
        
        // Clear selected files
        setSelectedFiles([]);
        
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Clear all sessions from IndexedDB
        await clearAllFromIndexedDB();
        
        // Also clear localStorage (for backward compatibility)
        try {
          localStorage.removeItem('uploadSessions');
        } catch (e) {
          // Ignore localStorage errors
        }
        
        console.log('Cleared all upload sessions and file selections on page reload');
      } catch (error) {
        console.error('Error clearing sessions on reload:', error);
      }
    };
    
    clearOnReload();
  }, []);

  // Get or initialize chunk size for a session
  const getChunkSize = (sessionId) => {
    if (!chunkSizeRef.current.has(sessionId)) {
      chunkSizeRef.current.set(sessionId, CHUNK_SIZE_DEFAULT);
    }
    return chunkSizeRef.current.get(sessionId);
  };

  // Update chunk size based on upload speed
  const updateChunkSize = (sessionId, chunkSize, uploadTimeMs) => {
    if (!uploadTimeMs || uploadTimeMs <= 0) return chunkSize;

    // Calculate upload speed (bytes per second)
    const uploadSpeed = (chunkSize / uploadTimeMs) * 1000; // bytes per second

    // Initialize or update speed metrics
    if (!uploadSpeedRef.current.has(sessionId)) {
      uploadSpeedRef.current.set(sessionId, []);
    }
    const speedHistory = uploadSpeedRef.current.get(sessionId);
    speedHistory.push({ speed: uploadSpeed, timestamp: Date.now() });

    // Keep only recent samples
    if (speedHistory.length > SPEED_SAMPLES_TO_CONSIDER) {
      speedHistory.shift();
    }

    // Calculate average speed from recent samples
    const avgSpeed = speedHistory.reduce((sum, sample) => sum + sample.speed, 0) / speedHistory.length;

    // Adjust chunk size based on average speed
    let newChunkSize = chunkSize;

    if (avgSpeed > SPEED_THRESHOLD_FAST) {
      // Fast connection - increase chunk size
      newChunkSize = Math.min(chunkSize + CHUNK_SIZE_STEP, CHUNK_SIZE_MAX);
    } else if (avgSpeed < SPEED_THRESHOLD_SLOW) {
      // Slow connection - decrease chunk size
      newChunkSize = Math.max(chunkSize - CHUNK_SIZE_STEP, CHUNK_SIZE_MIN);
    }

    // Update stored chunk size
    chunkSizeRef.current.set(sessionId, newChunkSize);

    return newChunkSize;
  };

  // Reset chunk size metrics (call on error or pause)
  const resetChunkSizeMetrics = (sessionId) => {
    uploadSpeedRef.current.delete(sessionId);
    // Keep chunk size but reset speed history
  };

  // Get current upload speed for a session (bytes per second)
  const getCurrentUploadSpeed = (sessionId) => {
    const speedHistory = uploadSpeedRef.current.get(sessionId);
    if (!speedHistory || speedHistory.length === 0) {
      return 0;
    }
    // Calculate average speed from recent samples
    const avgSpeed = speedHistory.reduce((sum, sample) => sum + sample.speed, 0) / speedHistory.length;
    return avgSpeed;
  };

  // Track upload time for average calculation
  const recordUploadTime = (sessionId, uploadTimeMs) => {
    if (!uploadTimeHistoryRef.current.has(sessionId)) {
      uploadTimeHistoryRef.current.set(sessionId, []);
    }
    const history = uploadTimeHistoryRef.current.get(sessionId);
    history.push(uploadTimeMs);
    // Keep only recent samples (same as speed samples)
    if (history.length > SPEED_SAMPLES_TO_CONSIDER) {
      history.shift();
    }
  };

  // Get average upload time for a session
  const getAverageUploadTime = (sessionId) => {
    const history = uploadTimeHistoryRef.current.get(sessionId);
    if (!history || history.length === 0) {
      return null; // No history yet
    }
    const avgTime = history.reduce((sum, time) => sum + time, 0) / history.length;
    return avgTime;
  };

  // Fetch with timeout wrapper
  const fetchWithTimeout = async (url, options, timeoutMs) => {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => {
      timeoutController.abort();
    }, timeoutMs);

    // Use the original signal if provided, otherwise create a new one
    // If original signal is aborted, the fetch will fail with AbortError
    const originalSignal = options.signal;
    
    try {
      // Create a combined signal that aborts if either timeout or original signal aborts
      let combinedSignal = originalSignal;
      
      // If we have both a timeout and an original signal, we need to combine them
      if (originalSignal) {
        // If original signal is already aborted, throw immediately
        if (originalSignal.aborted) {
          clearTimeout(timeoutId);
          throw new DOMException('The operation was aborted.', 'AbortError');
        }
        
        // Create a combined controller
        const combinedController = new AbortController();
        
        // Listen to original signal abort
        originalSignal.addEventListener('abort', () => {
          combinedController.abort();
        });
        
        // Listen to timeout abort
        timeoutController.signal.addEventListener('abort', () => {
          combinedController.abort();
        });
        
        combinedSignal = combinedController.signal;
      } else {
        // No original signal, just use timeout signal
        combinedSignal = timeoutController.signal;
      }

      const response = await fetch(url, {
        ...options,
        signal: combinedSignal
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Check if it was a timeout (not an abort from original signal)
      if (timeoutController.signal.aborted && (!originalSignal || !originalSignal.aborted)) {
        throw new Error(`Request timeout after ${timeoutMs}ms`);
      }
      
      // Otherwise, re-throw the original error (which might be AbortError from original signal)
      throw error;
    }
  };

  // Sleep utility for retry delays
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Save upload session to IndexedDB
  const saveUploadSession = async (session) => {
    try {
      await saveToIndexedDB(session);
    } catch (error) {
      console.error('Error saving upload session to IndexedDB:', error);
      // Don't throw - allow app to continue even if save fails
    }
  };

  // Update upload session in state and IndexedDB
  const updateUploadSession = (session) => {
    setUploads(prev => prev.map(u => 
      u.sessionId === session.sessionId ? session : u
    ));
    // Save to IndexedDB asynchronously (fire and forget)
    saveUploadSession(session).catch(err => {
      console.error('Failed to save session update:', err);
    });
  };

  // Initialize upload session
  const initializeUploadSession = async (file, projectId) => {
    // Safety check: validate file type before initialization
    if (!isFileTypeSupported(file)) {
      console.error(`Cannot initialize upload for unsupported file type: ${file.name} (${file.type || 'unknown type'})`);
      alert(`File "${file.name}" has an unsupported file type and cannot be uploaded.`);
      return;
    }

    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Store file reference
    fileRefsRef.current.set(sessionId, file);
    
    // Create upload session
    const uploadSession = {
      sessionId,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || 'application/octet-stream',
      projectId,
      uploadedBytes: 0,
      status: 'initializing',
      chunks: [],
      expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
    };

    setUploads(prev => [...prev, uploadSession]);
    saveUploadSession(uploadSession);

    try {
      // Initialize resumable upload session
      let initResponse;
      try {
        initResponse = await fetch(`${API_BASE_URL}/upload/resumable`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            fileSize: file.size,
            projectId,
            // Include form data if available
            ...(formData && {
              userName: formData.userName || '',
              userEmail: formData.userEmail || '',
              videoLanguage: formData.videoLanguage || '',
              videoCountry: formData.videoCountry || '',
              authorizationConsent: formData.authorizationConsent || false
            })
          })
        });
      } catch (fetchError) {
        // Handle connection errors specifically
        if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
          uploadSession.status = 'error';
          uploadSession.error = 'Cannot connect to server. Please make sure the server is running.';
          updateUploadSession(uploadSession);
          return;
        }
        throw fetchError;
      }

      if (!initResponse.ok) {
        let errorText;
        try {
          const errorData = await initResponse.json();
          errorText = errorData.error || errorData.message || initResponse.statusText;
        } catch (e) {
          errorText = await initResponse.text() || initResponse.statusText;
        }
        uploadSession.status = 'error';
        uploadSession.error = `Failed to initialize upload session: ${errorText}`;
        updateUploadSession(uploadSession);
        return;
      }

      const initData = await initResponse.json();
      uploadSession.serverSessionId = initData.sessionId;
      uploadSession.expiresAt = initData.expiresAt;
      uploadSession.status = 'ready';
      updateUploadSession(uploadSession);
    } catch (error) {
      console.error('Error initializing upload session:', error);
      uploadSession.status = 'error';
      uploadSession.error = error.message;
      updateUploadSession(uploadSession);
    }
  };

  // Upload file in chunks
  const uploadFile = async (uploadSession, file) => {
    const sessionId = uploadSession.sessionId;
    let currentChunkSize = getChunkSize(sessionId);
    
    // Create AbortController for this upload
    const abortController = new AbortController();
    abortControllersRef.current.set(sessionId, abortController);
    
    // Clear pause flag when starting new upload
    pauseStatusRef.current.set(sessionId, false);
    uploadSession.status = 'uploading';
    updateUploadSession(uploadSession);

    try {
      // Check if cancelled before starting
      if (abortController.signal.aborted) {
        uploadSession.status = 'cancelled';
        updateUploadSession(uploadSession);
        return;
      }

      // Upload file in chunks
      let startByte = uploadSession.uploadedBytes || 0;
      let chunkIndex = uploadSession.chunks?.length || 0;

      while (startByte < file.size) {
        // Check if cancelled or paused
        if (abortController.signal.aborted) {
          uploadSession.status = 'cancelled';
          updateUploadSession(uploadSession);
          return;
        }

        // Check if paused using ref
        if (pauseStatusRef.current.get(sessionId) === true) {
          uploadSession.status = 'paused';
          updateUploadSession(uploadSession);
          // Free up slot when paused
          activeUploadsRef.current.delete(sessionId);
          // Process queue to start next upload
          processQueue();
          return; // Exit the loop, upload is paused
        }

        // Get current chunk size (may have been adjusted)
        currentChunkSize = getChunkSize(sessionId);
        const endByte = Math.min(startByte + currentChunkSize, file.size);
        const chunk = file.slice(startByte, endByte);

        let chunkData = uploadSession.chunks?.[chunkIndex];
        if (!chunkData) {
          chunkData = {
            chunkIndex,
            startByte,
            endByte,
            size: chunk.size,
            uploaded: false
          };
          if (!uploadSession.chunks) {
            uploadSession.chunks = [];
          }
          uploadSession.chunks.push(chunkData);
        }

        // Optimistically update progress bar for better UX (will be corrected by server response)
        uploadSession.uploadedBytes = endByte;
        uploadSession.status = 'uploading';
        updateUploadSession(uploadSession);

        // Retry logic for chunk upload
        let retryAttempt = 0;
        let chunkResponse = null;
        let uploadTimeMs = 0;
        let lastError = null;

        while (retryAttempt <= MAX_RETRY_ATTEMPTS) {
          // Check if cancelled or paused before retry
          // IMPORTANT: Check pause status FIRST, then abort status
          // This ensures we distinguish pause from cancel correctly
          if (pauseStatusRef.current.get(sessionId) === true) {
            uploadSession.status = 'paused';
            updateUploadSession(uploadSession);
            return;
          }
          if (abortController.signal.aborted) {
            // If aborted but not paused, it's a cancel
            uploadSession.status = 'cancelled';
            updateUploadSession(uploadSession);
            abortControllersRef.current.delete(sessionId);
            pauseStatusRef.current.delete(sessionId);
            return;
          }

          try {
            const formData = new FormData();
            formData.append('chunk', chunk);
            formData.append('sessionId', uploadSession.serverSessionId);
            formData.append('startByte', startByte.toString());

            // Measure upload time for adaptive chunk sizing and timeout detection
            const uploadStartTime = Date.now();
            const avgUploadTime = getAverageUploadTime(sessionId);
            const timeoutMs = avgUploadTime 
              ? Math.max(CHUNK_UPLOAD_TIMEOUT_MS, avgUploadTime * RETRY_TIMEOUT_MULTIPLIER)
              : CHUNK_UPLOAD_TIMEOUT_MS;

            console.log(`[Upload] Chunk ${chunkIndex} (${startByte}-${endByte}) - Attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS + 1}, timeout: ${timeoutMs}ms${avgUploadTime ? `, avg: ${Math.round(avgUploadTime)}ms` : ''}`);

            try {
              chunkResponse = await fetchWithTimeout(
                `${API_BASE_URL}/upload/resumable/chunk`,
                {
                  method: 'POST',
                  body: formData,
                  signal: abortController.signal
                },
                timeoutMs
              );

              const uploadEndTime = Date.now();
              uploadTimeMs = uploadEndTime - uploadStartTime;
              
              // Record successful upload time
              recordUploadTime(sessionId, uploadTimeMs);
              
              console.log(`[Upload] Chunk ${chunkIndex} uploaded successfully in ${uploadTimeMs}ms`);

              // Check if upload took too long compared to average
              if (avgUploadTime && uploadTimeMs > avgUploadTime * RETRY_TIMEOUT_MULTIPLIER) {
                console.warn(`[Upload] Chunk ${chunkIndex} took ${uploadTimeMs}ms (${(uploadTimeMs / avgUploadTime).toFixed(1)}x average), but got response - continuing`);
              }

              // Success - break out of retry loop
              break;
            } catch (fetchError) {
              const uploadEndTime = Date.now();
              const attemptTime = uploadEndTime - uploadStartTime;
              lastError = fetchError;

              // Check if this was a pause or cancel BEFORE handling as error
              if (abortController.signal.aborted) {
                if (pauseStatusRef.current.get(sessionId) === true) {
                  // It was a pause - exit immediately, don't retry
                  console.log(`[Upload] Chunk ${chunkIndex} - Paused during upload`);
                  uploadSession.status = 'paused';
                  updateUploadSession(uploadSession);
                  return; // Exit the upload function
                } else {
                  // It was a cancel
                  console.log(`[Upload] Chunk ${chunkIndex} - Cancelled during upload`);
                  uploadSession.status = 'cancelled';
                  updateUploadSession(uploadSession);
                  abortControllersRef.current.delete(sessionId);
                  pauseStatusRef.current.delete(sessionId);
                  return; // Exit the upload function
                }
              }

              // Handle connection errors specifically
              if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
                console.error(`[Upload] Chunk ${chunkIndex} - Connection error after ${attemptTime}ms`);
                throw new Error('Connection lost. You can resume the upload later.');
              }
              
              // Handle abort errors (should be caught above, but just in case)
              if (fetchError.name === 'AbortError' || fetchError.message.includes('aborted')) {
                // Check pause status again
                if (pauseStatusRef.current.get(sessionId) === true) {
                  uploadSession.status = 'paused';
                  updateUploadSession(uploadSession);
                  return;
                }
                // Otherwise treat as cancel
                uploadSession.status = 'cancelled';
                updateUploadSession(uploadSession);
                abortControllersRef.current.delete(sessionId);
                pauseStatusRef.current.delete(sessionId);
                return;
              }
              
              // Handle timeout
              if (fetchError.message.includes('timeout')) {
                console.error(`[Upload] Chunk ${chunkIndex} - Timeout after ${attemptTime}ms (limit: ${timeoutMs}ms)`);
              } else {
                console.error(`[Upload] Chunk ${chunkIndex} - Error after ${attemptTime}ms:`, fetchError.message);
              }

              // Check if we should retry
              if (retryAttempt < MAX_RETRY_ATTEMPTS) {
                const delay = RETRY_DELAY_MS * Math.pow(2, retryAttempt); // Exponential backoff
                console.log(`[Upload] Chunk ${chunkIndex} - Retrying in ${delay}ms...`);
                await sleep(delay);
                retryAttempt++;
                continue; // Retry
              } else {
                // Max retries reached
                console.error(`[Upload] Chunk ${chunkIndex} - Max retries (${MAX_RETRY_ATTEMPTS}) reached`);
                throw fetchError;
              }
            }
          } catch (error) {
            // Check if this was a pause or cancel BEFORE retrying
            if (abortController.signal.aborted) {
              if (pauseStatusRef.current.get(sessionId) === true) {
                // It was a pause - exit immediately
                uploadSession.status = 'paused';
                updateUploadSession(uploadSession);
                return; // Exit the upload function
              } else {
                // It was a cancel
                uploadSession.status = 'cancelled';
                updateUploadSession(uploadSession);
                abortControllersRef.current.delete(sessionId);
                pauseStatusRef.current.delete(sessionId);
                return; // Exit the upload function
              }
            }

            // This catches errors from the retry loop
            if (retryAttempt < MAX_RETRY_ATTEMPTS) {
              const delay = RETRY_DELAY_MS * Math.pow(2, retryAttempt);
              console.log(`[Upload] Chunk ${chunkIndex} - Retrying after error in ${delay}ms...`);
              await sleep(delay);
              retryAttempt++;
              continue;
            } else {
              throw error;
            }
          }
        }

        // If we get here without a response, all retries failed
        if (!chunkResponse) {
          throw lastError || new Error('Failed to upload chunk after all retries');
        }

        // Process the response
        if (!chunkResponse.ok) {
          const errorText = await chunkResponse.text();
          
          // Handle 503 error - upload offset mismatch (server has different uploaded bytes)
          if (chunkResponse.status === 503 && errorText.includes('upload offset')) {
            console.warn(`[Upload] Chunk ${chunkIndex} - 503 offset mismatch, syncing with server...`);
            // Query server for actual uploaded bytes and sync
            try {
              const statusResponse = await fetch(`${API_BASE_URL}/upload/status/${uploadSession.serverSessionId}`);
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                // Update session with server's actual uploaded bytes
                uploadSession.uploadedBytes = statusData.uploadedBytes || 0;
                
                // Update chunks array to reflect what's actually uploaded
                if (uploadSession.chunks) {
                  uploadSession.chunks.forEach(chunk => {
                    if (chunk.endByte <= statusData.uploadedBytes) {
                      chunk.uploaded = true;
                    } else {
                      chunk.uploaded = false;
                    }
                  });
                }
                
                // Update the session state
                updateUploadSession(uploadSession);
                
                // Adjust startByte to match server's actual position
                startByte = statusData.uploadedBytes;
                
                // Recalculate chunkIndex based on new startByte
                if (uploadSession.chunks) {
                  const unuploadedChunk = uploadSession.chunks.find(chunk => !chunk.uploaded);
                  if (unuploadedChunk) {
                    chunkIndex = unuploadedChunk.chunkIndex;
                  } else {
                    chunkIndex = uploadSession.chunks.length;
                  }
                }
                
                console.log(`[Upload] Synced with server - resuming from byte ${startByte}`);
                // Continue with next iteration using corrected startByte
                continue;
              }
            } catch (statusError) {
              console.warn('[Upload] Could not fetch upload status after 503 error:', statusError);
            }
          }
          
          // Reset metrics on error to be conservative
          resetChunkSizeMetrics(sessionId);
          throw new Error(`Failed to upload chunk ${chunkIndex}: ${chunkResponse.status} ${errorText || chunkResponse.statusText}`);
        }

        const chunkResult = await chunkResponse.json();
        chunkData.uploaded = true;
        // Always use server's reported uploadedBytes - this is the source of truth
        uploadSession.uploadedBytes = chunkResult.uploadedBytes || endByte;
        uploadSession.status = 'uploading'; // Ensure status stays as uploading

        // Update chunk size based on upload performance
        currentChunkSize = updateChunkSize(sessionId, currentChunkSize, uploadTimeMs);
        
        // Update current upload speed in session state
        uploadSession.currentSpeed = getCurrentUploadSpeed(sessionId);
        updateUploadSession(uploadSession);

        if (chunkResult.completed) {
          uploadSession.status = 'completed';
          uploadSession.fileId = chunkResult.fileId;
          uploadSession.currentSpeed = 0; // Clear speed when completed
          updateUploadSession(uploadSession);
          abortControllersRef.current.delete(sessionId);
          pauseStatusRef.current.delete(sessionId);
          fileRefsRef.current.delete(sessionId); // Clean up file reference
          chunkSizeRef.current.delete(sessionId); // Clean up chunk size
          uploadSpeedRef.current.delete(sessionId); // Clean up speed metrics
          uploadTimeHistoryRef.current.delete(sessionId); // Clean up upload time history
          activeUploadsRef.current.delete(sessionId); // Remove from active uploads
          // Process queue to start next upload
          processQueue();
          break;
        }

        startByte = endByte;
        chunkIndex++;
      }
    } catch (error) {
      if (error.name === 'AbortError' || abortController.signal.aborted) {
        // Check if this was a pause or a cancel
        if (pauseStatusRef.current.get(sessionId) === true) {
          // It was a pause, status already set in pauseUpload function
          // Don't delete refs, keep them for resume
          return;
        } else {
          // It was a cancel
          uploadSession.status = 'cancelled';
          updateUploadSession(uploadSession);
          abortControllersRef.current.delete(sessionId);
          pauseStatusRef.current.delete(sessionId);
          activeUploadsRef.current.delete(sessionId); // Remove from active uploads
          // Process queue to start next upload
          processQueue();
          return;
        }
      }
      
      console.error('Error uploading file:', error);
      // Reset metrics on error to be conservative
      resetChunkSizeMetrics(sessionId);
      
      // Provide user-friendly error messages
      let errorMessage = error.message;
      if (error.message.includes('Cannot connect to server') || error.message.includes('Connection lost')) {
        errorMessage = error.message;
      } else if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = 'Cannot connect to server. Please make sure the server is running.';
      }
      
      uploadSession.status = 'error';
      uploadSession.error = errorMessage;
      updateUploadSession(uploadSession);
      
      // Don't delete refs on connection errors - allow resume
      if (!errorMessage.includes('Cannot connect') && !errorMessage.includes('Connection lost')) {
        abortControllersRef.current.delete(sessionId);
        pauseStatusRef.current.delete(sessionId);
        activeUploadsRef.current.delete(sessionId); // Remove from active uploads
        // Process queue to start next upload
        processQueue();
      } else {
        // Connection error - remove from active but keep in queue for resume
        activeUploadsRef.current.delete(sessionId);
        processQueue();
      }
      // Keep file reference for potential resume after error or pause
    }
  };

  // Process queue - start next queued upload if under limit
  const processQueue = () => {
    // Use setTimeout to avoid nested state updates and ensure we have latest state
    setTimeout(() => {
      setUploads(currentUploads => {
        // Find queued uploads for the selected project
        const projectUploads = currentUploads.filter(u => u.projectId === selectedProject);
        const queuedUploads = projectUploads.filter(u => u.status === 'queued');
        
        // Check how many slots are available
        const availableSlots = MAX_CONCURRENT_UPLOADS - activeUploadsRef.current.size;
        
        if (availableSlots > 0 && queuedUploads.length > 0) {
          // Start up to availableSlots queued uploads
          const toStart = queuedUploads.slice(0, availableSlots);
          toStart.forEach(upload => {
            // Mark as ready and start
            const updatedUpload = { ...upload, status: 'ready' };
            // Save to IndexedDB
            saveUploadSession(updatedUpload).catch(err => {
              console.error('Failed to save session update:', err);
            });
            // Start upload asynchronously
            startUpload(updatedUpload).catch(err => {
              console.error('Error starting queued upload:', err);
            });
          });
          // Return updated uploads
          return currentUploads.map(u => {
            const toStartUpload = toStart.find(s => s.sessionId === u.sessionId);
            return toStartUpload ? { ...u, status: 'ready' } : u;
          });
        }

        // Check if all uploads for the project have settled (no in-progress work left)
        const hasInProgress = projectUploads.some(u =>
          u.status === 'queued' || u.status === 'uploading' || u.status === 'ready' || u.status === 'initializing'
        );

        if (projectUploads.length > 0 && !hasInProgress && activeUploadsRef.current.size === 0) {
          const completed = projectUploads.filter(u => u.status === 'completed');
          const failed = projectUploads.filter(u => u.status === 'error');
          const cancelled = projectUploads.filter(u => u.status === 'cancelled');

          // Fire callback outside of setState to avoid side-effects during render
          if (onAllUploadsSettledRef.current) {
            setTimeout(() => {
              onAllUploadsSettledRef.current({ completed, failed, cancelled });
            }, 0);
          }
        }
        
        return currentUploads;
      });
    }, 0);
  };

  // Start upload
  const startUpload = async (uploadSession) => {
    if (uploadSession.status !== 'ready' && uploadSession.status !== 'paused' && uploadSession.status !== 'error') {
      return;
    }

    // Check if we're at max concurrent uploads
    if (activeUploadsRef.current.size >= MAX_CONCURRENT_UPLOADS) {
      // Set to queued status
      const updatedSession = { ...uploadSession, status: 'queued' };
      updateUploadSession(updatedSession);
      return;
    }

    // Get stored file reference
    const file = fileRefsRef.current.get(uploadSession.sessionId);
    if (!file) {
      alert('File reference not found. Please re-select the file.');
      return;
    }

    // Verify file still matches
    if (file.name !== uploadSession.fileName || file.size !== uploadSession.fileSize) {
      alert('File has changed. Please re-select the file.');
      return;
    }

    // Add to active uploads
    activeUploadsRef.current.add(uploadSession.sessionId);

    // Start the actual upload
    await uploadFile(uploadSession, file);
  };

  // Resume upload
  const resumeUpload = async (uploadSession) => {
    if (!uploadSession.serverSessionId) {
      alert('Cannot resume: session not initialized');
      return;
    }

    // Don't resume if already completed or cancelled
    if (uploadSession.status === 'completed' || uploadSession.status === 'cancelled') {
      return;
    }

    // Get stored file reference
    const file = fileRefsRef.current.get(uploadSession.sessionId);
    if (!file) {
      alert('File reference not found. Please re-upload the file.');
      return;
    }

    // Verify file still matches (name and size)
    if (file.name !== uploadSession.fileName || file.size !== uploadSession.fileSize) {
      alert('File has changed. Please re-upload the file.');
      return;
    }

    // Query server for actual uploaded bytes to sync client state
    try {
      const statusResponse = await fetch(`${API_BASE_URL}/upload/status/${uploadSession.serverSessionId}`);
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        // Update session with server's actual uploaded bytes
        uploadSession.uploadedBytes = statusData.uploadedBytes || 0;
        
        // Also update chunks array to reflect what's actually uploaded
        if (uploadSession.chunks) {
          // Mark chunks as uploaded if they're before the server's uploaded bytes
          uploadSession.chunks.forEach(chunk => {
            if (chunk.endByte <= statusData.uploadedBytes) {
              chunk.uploaded = true;
            }
          });
        }
      }
    } catch (error) {
      console.warn('Could not fetch upload status from server, using client-side value:', error);
      // Continue with client-side value if status check fails
    }

    // Clear pause flag
    pauseStatusRef.current.set(uploadSession.sessionId, false);
    
    // Check if we're at max concurrent uploads (paused uploads freed their slot)
    if (activeUploadsRef.current.size >= MAX_CONCURRENT_UPLOADS) {
      // Set to queued status - need to wait for a slot
      const updatedSession = { ...uploadSession, status: 'queued' };
      updateUploadSession(updatedSession);
      return;
    }

    // Add to active uploads
    activeUploadsRef.current.add(uploadSession.sessionId);
    
    // Update status to uploading
    const updatedSession = { ...uploadSession, status: 'uploading' };
    updateUploadSession(updatedSession);

    // Continue uploading from the correct position (now synced with server)
    await uploadFile(updatedSession, file);
  };

  // Pause upload
  const pauseUpload = (uploadSession) => {
    // Set pause flag FIRST so error handlers can distinguish pause from cancel
    pauseStatusRef.current.set(uploadSession.sessionId, true);
    
    // Abort the current fetch request to pause immediately
    const abortController = abortControllersRef.current.get(uploadSession.sessionId);
    if (abortController && !abortController.signal.aborted) {
      abortController.abort();
    }
    
    // Free up slot when paused - allows other files to start uploading
    const wasActive = activeUploadsRef.current.delete(uploadSession.sessionId);
    
    // Update status
    const updatedSession = { ...uploadSession, status: 'paused' };
    updateUploadSession(updatedSession);
    
    // Process queue to start next upload if slot was freed
    if (wasActive) {
      processQueue();
    }
  };

  // Cancel upload
  const cancelUpload = (uploadSession) => {
    // Abort the current fetch request
    const abortController = abortControllersRef.current.get(uploadSession.sessionId);
    if (abortController) {
      abortController.abort();
      abortControllersRef.current.delete(uploadSession.sessionId);
    }
    
    // Clear pause status
    pauseStatusRef.current.delete(uploadSession.sessionId);
    
    // Remove from active uploads
    activeUploadsRef.current.delete(uploadSession.sessionId);
    
    // Clean up file reference (no need to keep it after cancel)
    fileRefsRef.current.delete(uploadSession.sessionId);
    
    // Clean up chunk size and speed metrics
    chunkSizeRef.current.delete(uploadSession.sessionId);
    uploadSpeedRef.current.delete(uploadSession.sessionId);
    
    // Mark as cancelled but keep the upload info visible
    const updatedSession = { ...uploadSession, status: 'cancelled' };
    updateUploadSession(updatedSession);
    
    // Process queue to start next upload
    processQueue();
  };

  // Delete upload
  const deleteUpload = async (uploadSession) => {
    const sessionId = uploadSession.sessionId;
    
    // Abort if currently uploading
    const abortController = abortControllersRef.current.get(sessionId);
    if (abortController) {
      abortController.abort();
    }
    
    // Remove from active uploads to free up the slot
    const wasActive = activeUploadsRef.current.delete(sessionId);
    
    // Clean up ALL references - ensure nothing is left behind
    abortControllersRef.current.delete(sessionId);
    pauseStatusRef.current.delete(sessionId);
    fileRefsRef.current.delete(sessionId);
    chunkSizeRef.current.delete(sessionId);
    uploadSpeedRef.current.delete(sessionId);
    uploadTimeHistoryRef.current.delete(sessionId);
    
    // Remove from uploads array
    setUploads(prev => prev.filter(u => u.sessionId !== sessionId));
    
    // Remove from IndexedDB
    try {
      await deleteFromIndexedDB(sessionId);
      // Also delete by serverSessionId if different
      if (uploadSession.serverSessionId && uploadSession.serverSessionId !== sessionId) {
        const allSessions = await getAllUploadSessions();
        const matchingSession = allSessions.find(s => s.serverSessionId === uploadSession.serverSessionId);
        if (matchingSession) {
          await deleteFromIndexedDB(matchingSession.sessionId);
        }
      }
    } catch (error) {
      console.error('Error removing upload session from IndexedDB:', error);
    }
    
    // If this was an active upload, process queue to start next upload
    if (wasActive) {
      processQueue();
    }
  };

  // Dismiss error for an upload session
  const dismissError = (uploadSession) => {
    const updatedSession = { ...uploadSession, error: null };
    // If status is 'error', change it back to the previous state (ready, paused, etc.)
    // But keep it as 'error' if we want to preserve the state
    // Actually, let's keep status as 'error' but just clear the error message
    updateUploadSession(updatedSession);
  };

  // Clear all uploads
  const clearAllUploads = async () => {
    // Confirm before clearing all
    if (!window.confirm('Are you sure you want to clear all uploads from the list?')) {
      return;
    }
    
    // Abort all active uploads
    abortControllersRef.current.forEach((controller) => {
      if (controller) {
        controller.abort();
      }
    });
    
    // Clear all references - free all slots
    abortControllersRef.current.clear();
    pauseStatusRef.current.clear();
    fileRefsRef.current.clear();
    chunkSizeRef.current.clear();
    uploadSpeedRef.current.clear();
    uploadTimeHistoryRef.current.clear();
    activeUploadsRef.current.clear(); // Free all slots
    
    // Clear uploads array
    setUploads([]);
    
    // Clear IndexedDB
    try {
      await clearAllFromIndexedDB();
    } catch (error) {
      console.error('Error clearing upload sessions from IndexedDB:', error);
    }
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);

    if (!selectedProject || files.length === 0) {
      // Clear file input if no project selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFiles([]);
      return;
    }

    // Validate file types - filter out unsupported files
    const unsupportedFiles = files.filter(file => !isFileTypeSupported(file));
    const supportedFiles = files.filter(file => isFileTypeSupported(file));

    if (unsupportedFiles.length > 0) {
      const fileNames = unsupportedFiles.map(f => f.name).join(', ');
      const fileTypes = unsupportedFiles.map(f => f.type || 'unknown type').join(', ');
      alert(`The following files are not supported and will not be uploaded:\n${fileNames}\n\nFile types: ${fileTypes}\n\nOnly video, audio, image, document, and archive formats are supported.`);
      
      // If all files are unsupported, clear and return
      if (supportedFiles.length === 0) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFiles([]);
        return;
      }
    }

    // Continue with only supported files
    const filesToProcess = supportedFiles;

    // Check if any files are already in non-completed states with a different project
    const filesWithDifferentProject = filesToProcess.filter(file => {
      return uploads.some(u => 
        u.fileName === file.name && 
        u.fileSize === file.size && 
        u.projectId !== selectedProject &&
        (u.status === 'ready' || u.status === 'initializing' || u.status === 'uploading' || u.status === 'paused' || u.status === 'error')
      );
    });

    if (filesWithDifferentProject.length > 0) {
      const fileNames = filesWithDifferentProject.map(f => f.name).join(', ');
      alert(`The following files are already being processed for a different project and cannot be changed: ${fileNames}`);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Filter out files that are already completed for the CURRENT project
    const filesToInitialize = filesToProcess.filter(file => {
      const isCompleted = uploads.some(u => 
        u.fileName === file.name && 
        u.fileSize === file.size && 
        u.projectId === selectedProject &&
        u.status === 'completed'
      );
      return !isCompleted;
    });

    const skippedFiles = filesToProcess.filter(file => {
      const isCompleted = uploads.some(u => 
        u.fileName === file.name && 
        u.fileSize === file.size && 
        u.projectId === selectedProject &&
        u.status === 'completed'
      );
      return isCompleted;
    });

    if (skippedFiles.length > 0 && filesToInitialize.length === 0) {
      // All selected files are already completed for this project
      const fileNames = skippedFiles.map(f => f.name).join(', ');
      alert(`The following files have already been uploaded to this project and will not be re-initialized: ${fileNames}`);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (skippedFiles.length > 0) {
      // Some files are completed for this project, show info but continue with others
      const fileNames = skippedFiles.map(f => f.name).join(', ');
      console.log(`Skipping already completed files for this project: ${fileNames}`);
    }

    // Set selected files (only supported, non-completed files will be initialized)
    const filesToSet = filesToInitialize.length > 0 ? filesToInitialize : filesToProcess;
    setSelectedFiles(filesToSet);
    
    // Track which project these files were selected for
    if (filesToSet.length > 0) {
      filesSelectedForProjectRef.current = selectedProject;
    }

    // Clear file input after files are selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Initialize uploads when project is selected and files are already selected
  useEffect(() => {
    if (selectedProject && selectedFiles.length > 0) {
      // Only initialize if files were selected for the current project
      if (filesSelectedForProjectRef.current !== selectedProject) {
        return; // Files were selected for a different project, don't auto-initialize
      }
      
      selectedFiles.forEach(file => {
        const fileName = file.name;
        const fileSize = file.size;
        
        // Skip if file is already completed for the CURRENT project
        const isCompleted = uploads.some(u => 
          u.fileName === fileName && 
          u.fileSize === fileSize && 
          u.projectId === selectedProject &&
          u.status === 'completed'
        );
        
        if (isCompleted) {
          return; // Skip this file - it's already completed for this project
        }
        
        // Check if already initialized for current project
        const alreadyInitialized = uploads.some(u => 
          u.fileName === fileName && 
          u.fileSize === fileSize && 
          u.projectId === selectedProject &&
          (u.status === 'ready' || u.status === 'initializing' || u.status === 'uploading' || u.status === 'paused' || u.status === 'error')
        );
        
        if (!alreadyInitialized) {
          initializeUploadSession(file, selectedProject);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedFiles]);

  // Clear selected files when project changes
  useEffect(() => {
    // Check if there are unuploaded files for the current project
    const hasUnuploadedFiles = () => {
      if (!selectedProject) return false;
      const projectUploads = uploads.filter(u => u.projectId === selectedProject);
      return projectUploads.some(u => 
        u.status !== 'completed' && u.status !== 'cancelled'
      );
    };
    
    // Don't clear files if there are unuploaded files - prevent project change instead
    if (hasUnuploadedFiles()) {
      return;
    }
    
    // When project changes, clear selectedFiles
    setSelectedFiles([]);
    
    // Clear the ref that tracks which project files were selected for
    filesSelectedForProjectRef.current = null;
    
    // Clear file input when project changes
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // Handle submit - queue all ready uploads (up to max concurrent)
  const handleSubmit = async () => {
    if (!selectedProject) {
      alert('Please select a project');
      return;
    }

    // Get uploads for the current project only
    const currentProjectUploads = uploads.filter(u => u.projectId === selectedProject);
    
    if (currentProjectUploads.length === 0) {
      alert('Please select at least one file for the current project');
      return;
    }

    setLoading(true);

    // Get ready uploads for the current project
    const readyUploads = currentProjectUploads.filter(u => u.status === 'ready');
    
    // Start uploads up to max concurrent, queue the rest
    const availableSlots = MAX_CONCURRENT_UPLOADS - activeUploadsRef.current.size;
    const toStartNow = readyUploads.slice(0, availableSlots);
    const toQueue = readyUploads.slice(availableSlots);
    
    // Start immediate uploads
    const uploadPromises = toStartNow.map(upload => startUpload(upload));
    
    // Queue the rest
    toQueue.forEach(upload => {
      const updatedSession = { ...upload, status: 'queued' };
      updateUploadSession(updatedSession);
    });
    
    // Wait for immediate uploads to start (not complete)
    await Promise.allSettled(uploadPromises);

    setLoading(false);
  };

  return {
    uploads,
    selectedFiles,
    loading,
    fileInputRef,
    handleFileSelect,
    handleSubmit,
    startUpload,
    resumeUpload,
    pauseUpload,
    cancelUpload,
    deleteUpload,
    dismissError,
    clearAllUploads,
  };
}

